package meineglasfaser.frontend.admin.pages.shop

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.navigation.Anchor
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import meineglasfaser.api.*
import meineglasfaser.api.routes.AdminRoutes
import meineglasfaser.frontend.admin.components.widgets.YaTable
import meineglasfaser.frontend.admin.components.widgets.named
import meineglasfaser.frontend.admin.components.widgets.sortedDesc
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.*
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import org.jetbrains.compose.web.dom.*

@Page("bestellungen")
@Composable
fun OrdersPage(ctx: PageContext) {
    val orderNid = ctx.route.fragment?.toIntOrNull()

    YaTable(
        header = { H1 { Text("Bestellungen") } },
        idProperty = OrderPaginated::id,
        columns = listOf(
            OrderPaginated::nid named "Nr.",
            OrderPaginated::customerName named "Kunde",
            OrderPaginated::deliveryAddress named "Lieferadresse",
            OrderPaginated::createdAt named "Aufgegeben am",
        ),
        initialOptions = YaTableOptions.DEFAULT.copy(
            orderBy = listOf(
                OrderPaginated::nid.sortedDesc(),
            ),
        ),
        fetch = { options ->
            BackendApi.post(AdminRoutes.Orders()) {
                withAuth()
                setJsonBody(options)
            }
        },
        onViewEntry = { entry ->
            ctx.router.navigateTo("#${entry.nid}")
        },
    )

    if (orderNid != null) {
        OrderModal(orderNid) {
            ctx.router.navigateTo("#")
        }
    }
}

@Composable
fun OrderModal(orderNid: Int, onHide: () -> Unit) {
    val orderWithEntries = rememberQuery<OrderWithEntries?>(null, orderNid) {
        BackendApi.get(AdminRoutes.Orders.ByNid(orderNid)) {
            withAuth()
        }
    }

    if (orderWithEntries == null) {
        return
    }

    val order = orderWithEntries.order
    val entries = orderWithEntries.entries
    Modal(onHide, size = ModalSize.LG) {
        Header {
            ModalCloseBtn(onHide)
            Text("Bestellung #${order.nid}")
        }

        Div({ classes("row") }) {
            Div({ classes("col-12", "col-md-6") }) {
                OrderCustomerArticle(order)
            }
            Div({ classes("col-12", "col-md-6") }) {
                OrderAddressArticle(order)
            }
        }

        OrderEntriesArticle(entries)
    }
}

@Composable
private fun OrderCustomerArticle(order: Order) {
    val customer = order.customer
    val legacyCustomer = order.customerLegacyData

    Article {
        Header { Text("Kundendaten") }
        if (customer != null) {
            Div {
                Text("Kundennummer: ")
                Span { Text(customer.nid.toString()) }
            }
            Div {
                Text("Name: ")
                Span { Text(order.customerName) }
            }
            Div {
                Text("Kundentyp: ")
                when (customer.type) {
                    CustomerType.INDIVIDUAL -> Text("Privatkunde")
                    CustomerType.COMPANY -> Text("Geschäftskunde")
                }
            }
            Div {
                Text("E-Mail: ")
                Anchor("mailto:${customer.accountEmail}") {
                    Text(customer.accountEmail)
                }
            }
            Div {
                Text("Festnetz: ")
                if (!customer.telFixedLine.isNullOrBlank()) {
                    Anchor("tel:${customer.telFixedLine}") {
                        Text(customer.telFixedLine!!)
                    }
                } else {
                    Text("-")
                }
            }
            Div {
                Text("Mobilfunk: ")
                if (!customer.telMobile.isNullOrBlank()) {
                    Anchor("tel:${customer.telMobile}") {
                        Text(customer.telMobile!!)
                    }
                } else {
                    Text("-")
                }
            }
            Anchor("/kundenstamm/#${customer.nid}", openInternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB) {
                Text("Stammblatt anzeigen")
            }
        } else if (legacyCustomer != null) {
            Div {
                Text("(Legacy, aus Migration übernommene Daten)")
            }
            Div {
                Text("Name: ")
                val name = listOfNotNull(
                    legacyCustomer.individualFirstName,
                    legacyCustomer.individualLastName
                ).joinToString(" ")
                Text(name.ifBlank { "-" })
            }
            Div {
                Text("E-Mail: ")
                if (!legacyCustomer.email.isNullOrBlank()) {
                    Anchor("mailto:${legacyCustomer.email}") {
                        Text(legacyCustomer.email!!)
                    }
                } else {
                    Text("-")
                }
            }
        }
    }
}

@Composable
private fun OrderAddressArticle(order: Order) {
    Article {
        Header { Text("Lieferadresse") }
        UBLPostalAddressView(
            address = order.deliveryAddress,
            vertical = true,
            showViewOnMapsLink = true,
        )
    }
}

@Composable
private fun OrderEntriesArticle(entries: List<OrderEntry>) {
    Article {
        Header { Text("In der Bestellung enthaltene Produkte") }
        Table {
            Thead {
                Tr {
                    Th { Text("Pos.") }
                    Th { Text("Produkt") }
                    Th { Text("Menge") }
                    Th { Text("Preis (exkl MwSt)") }
                }
            }
            Tbody {
                entries.forEach { entry ->
                    key(entry) {
                        Tr {
                            Th { Text(entry.position.toString()) }
                            Th {
                                Text(entry.product.title)
                                if (entry.isAddon) {
                                    Text(" (Addon)")
                                }
                            }
                            Th { Text(entry.quantity.toString()) }
                            Th {
                                PriceGroupView(
                                    price = ShopPriceGroup(
                                        monthly = entry.product.monthlyPrice,
                                        yearly = entry.product.yearlyPrice,
                                        once = entry.product.oncePrice,
                                    )
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
